import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_CURRENT_PAGE,
  handlePagination,
} from '@client/utils/table/pagination.util';

import type { EAntSort, ITableParams } from '@client/types/antd.type';
import type {
  FilterValue,
  TablePaginationConfig,
} from 'antd/es/table/interface';

export type TFilters = Record<string, FilterValue | null>;

export type TSortItem = {
  columnKey?: string;
  order?: EAntSort | null;
};
// Support AntD single or multiple sorters
export type TSorts = TSortItem | TSortItem[] | null;

export type TDefaults = {
  current?: number;
  pageSize?: number;
  sizeChanger?: number;
  defaultCurrent?: number;

  /**
   * Whether the hook should read the initial state from the URL search params and
   * mirror subsequent changes back. Defaults to `true`. Set to `false` for
   * tables mounted inside a drawer or modal so their pagination/sort/filter
   * state stays local and does not pollute the host page's URL.
   */
  syncUrl?: boolean;
};

export type TUseTable = {
  tableParams: ITableParams;
  filteredInfo: TFilters;
  sortedInfo: TSorts;
  // U1: sync — AntD Table.onChange expects void, not Promise<void>
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue>,
    sorter: unknown,
  ) => void;
};

// U6: pure helpers extracted from effect body — module-level, independently testable

const toSortArray = (s: TSorts): TSortItem[] => {
  if (!s) return [];
  return Array.isArray(s) ? s : [s];
};

const isEmptyFilters = (f: TFilters): boolean => {
  if (!f) return true;
  const entries = Object.entries(f);
  if (entries.length === 0) return true;
  return entries.every(
    ([, v]) => v === null || (Array.isArray(v) && v.length === 0),
  );
};

// U7: filter normalisation extracted for readability and testability
const normaliseFilters = (f: TFilters): Record<string, string[]> =>
  Object.entries(f)
    .filter(([, v]) => Array.isArray(v) && v.length > 0)
    .reduce(
      (acc, [k, v]) => {
        const uniq = [
          ...new Set(
            (v as (string | number | boolean)[]).map((x) => String(x)),
          ),
        ];
        if (uniq.length > 0) acc[k] = uniq;
        return acc;
      },
      {} as Record<string, string[]>,
    );

/**
 * Hook for fetching data for a table. It fetches data by calling a server-side method (first argument).
 * @param {[]} entities Array of entities to be displayed in the table.
 * @param {number} total Total number of entities (for pagination).
 * @param {TDefaults} defaults Default values for current and pageSize.
 * @returns {TUseTable} Object with `tableParams`, `filteredInfo`, `sortedInfo` and `handleTableChange`.
 */
export const useTable = <T>(
  entities: T[],
  total: number,
  defaults?: TDefaults,
): TUseTable => {
  const syncUrl = defaults?.syncUrl ?? true;
  const defaultCurrent = defaults?.current;
  const defaultPageSize = defaults?.pageSize;

  const [searchParams, setSearchParams] = useSearchParams();

  // `setSearchParams` from react-router v7 is recreated whenever the URL changes
  // (its useCallback depends on `searchParams`). Holding it through a ref keeps
  // the URL-sync effect's dependency array stable and prevents an infinite
  // re-run loop when other state changes around it.
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  }, [setSearchParams]);

  // U3: memoised URL parsing — deps are primitives so an unstable `defaults` object
  // reference from callers does not bust the memo on every render.
  // These values are init-time defaults; after mount the internal state is authoritative.
  const {
    resolvedCurrent,
    resolvedPageSize,
    resolvedSorters,
    resolvedFilters,
  } = useMemo(() => {
    const rc = (() => {
      if (!syncUrl) return defaultCurrent ?? DEFAULT_CURRENT_PAGE;
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0
        ? fromUrl
        : (defaultCurrent ?? DEFAULT_CURRENT_PAGE);
    })();

    const rps = (() => {
      if (!syncUrl) return defaultPageSize ?? DEFAULT_PAGE_SIZE;
      const fromUrl = Number(searchParams.get('pageSize'));
      return Number.isFinite(fromUrl) && fromUrl > 0
        ? fromUrl
        : (defaultPageSize ?? DEFAULT_PAGE_SIZE);
    })();

    const rs: TSorts = (() => {
      if (!syncUrl) return {} as TSorts;
      const sortRaw = searchParams.get('sort');
      if (sortRaw) {
        try {
          const parsed = JSON.parse(sortRaw);
          const isSortItem = (v: unknown): v is TSortItem =>
            typeof v === 'object' &&
            v !== null &&
            'columnKey' in (v as Record<string, unknown>) &&
            'order' in (v as Record<string, unknown>);
          if (Array.isArray(parsed)) {
            const arr = parsed.filter(
              (s): s is TSortItem =>
                isSortItem(s) && !!s.columnKey && !!s.order,
            );
            if (arr.length) return arr as TSorts;
          }
        } catch {
          // ignore malformed JSON
        }
      }
      const legacyKey = searchParams.get('sortKey') || undefined;
      const legacyOrder =
        (searchParams.get('sortOrder') as EAntSort) || undefined;
      return (
        legacyKey && legacyOrder
          ? { columnKey: legacyKey, order: legacyOrder }
          : {}
      ) as TSorts;
    })();

    const rf: TFilters = (() => {
      if (!syncUrl) return {} as TFilters;
      const raw = searchParams.get('filters');
      if (!raw) return {} as TFilters;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') return parsed as TFilters;
      } catch {
        // ignore malformed JSON
      }
      return {} as TFilters;
    })();

    return {
      resolvedCurrent: rc,
      resolvedPageSize: rps,
      resolvedSorters: rs,
      resolvedFilters: rf,
    };
  }, [searchParams, syncUrl, defaultCurrent, defaultPageSize]);

  // U8: state declared before the handler that sets it
  const [pagination, setPagination] = useState({
    current: resolvedCurrent,
    pageSize: resolvedPageSize,
  });

  const [filteredInfo, setFilteredInfo] = useState<TFilters>(resolvedFilters);
  const [sortedInfo, setSortedInfo] = useState<TSorts>(resolvedSorters);

  // U4: resolvedCurrent/resolvedPageSize removed from deps — pagination state is the
  // source of truth after mount; the || fallbacks were dead code once pagination is set.
  const tableParams = useMemo(
    () => ({
      pagination: handlePagination<T>({
        entities,
        total,
        pagination: {
          current: pagination.current,
          pageSize: pagination.pageSize,
        },
      }),
      filters: filteredInfo,
    }),
    [entities, pagination, total, filteredInfo],
  );

  // U4: resolvedCurrent/resolvedPageSize removed from deps and write path.
  useEffect(() => {
    if (!syncUrl) return;

    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);

        next.set('page', String(pagination.current));
        next.set('pageSize', String(pagination.pageSize));

        // Always remove legacy keys; use `sort` param exclusively
        next.delete('sortKey');
        next.delete('sortOrder');

        const sortArray = toSortArray(sortedInfo)
          .filter((s) => !!s && !!s.columnKey && !!s.order)
          .map((s) => ({
            columnKey: s.columnKey as string,
            order: s.order as EAntSort,
          }));

        if (sortArray.length > 0) {
          next.set('sort', JSON.stringify(sortArray));
        } else {
          next.delete('sort');
        }

        if (isEmptyFilters(filteredInfo)) {
          next.delete('filters');
        } else {
          const normalized = normaliseFilters(filteredInfo);
          if (Object.keys(normalized).length === 0) {
            next.delete('filters');
          } else {
            next.set('filters', JSON.stringify(normalized));
          }
        }

        return next;
      },
      { replace: true },
    );
  }, [filteredInfo, pagination, sortedInfo, syncUrl]);

  // U8: declared after the state it sets; U1: sync; U2: stable ref via useCallback
  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue>,
      sorter: unknown,
    ) => {
      setPagination({
        current: pagination.current ?? DEFAULT_CURRENT_PAGE,
        pageSize: pagination.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      setFilteredInfo(filters);
      setSortedInfo(sorter as TSorts);
    },
    [],
  );

  return {
    tableParams,
    filteredInfo,
    sortedInfo,
    handleTableChange,
  };
};
